import * as Yup from 'yup';
import { useState } from 'react';
// next
import NextLink from 'next/link';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField } from '../../../components/hook-form';
import getAuthErrorMessage from 'src/utils/getAuthErrorMessage';
import { useSnackbar } from 'notistack';

// ----------------------------------------------------------------------

type FormValuesProps = {
  email: string;
  password: string;
  afterSubmit?: string;
};

export default function LoginForm() {
  const { login } = useAuth();

  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email('有効なメールアドレスを使用してください。')
      .required('メールアドレスは必須です。'),
    password: Yup.string().required('パスワードは必須です。'),
  });

  const defaultValues = {
    email: '',
    password: '',
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    try {
      await login(data.email, data.password);
    } catch (error) {
      // Determine error code/name from AWS SDK error object
      const errorCode =
        (error && (error as any).code) ||
        (error && (error as any).name) ||
        (typeof error === 'string' ? error : undefined);

      // Field-level validation messages
      if (errorCode === 'UserNotFoundException') {
        // Email does not exist
        setError('email', {
          type: 'manual',
          message: 'このメールアドレスは登録されていません。',
        });
        return;
      }

      if (errorCode === 'NotAuthorizedException') {
        // Check if user is disabled (deleted user)
        const errorMessage = (error as any)?.message || '';
        if (errorMessage.includes('User is disabled')) {
          enqueueSnackbar(
            'このアカウントは無効化されています。\n再度アクティブにしたい場合は管理者にお問い合わせください。',
            { variant: 'error' }
          );
          return;
        }

        // Incorrect password (or masked error when user enumeration is prevented)
        setError('password', {
          type: 'manual',
          message: 'パスワードが正しくありません。',
        });
        return;
      }

      // checkUserServiceからのエラータイプを処理
      const errorType = (error as any).type;
      if (errorType === 'AccountDeleted' || errorType === 'USER_DELETED') {
        enqueueSnackbar(
          'このメールアドレスでアクティブなアカウントが存在しません。\n再度アクティブにしたい場合は管理者の方に問い合わせてください。',
          { variant: 'error' }
        );
      } else if (errorType === 'ORGANIZATION_NOT_FOUND') {
        enqueueSnackbar('組織情報が見つかりません。\n管理者にお問い合わせください。', {
          variant: 'error',
        });
      } else if (errorType === 'CREATE_FAILED') {
        enqueueSnackbar(
          'ユーザー情報の作成に失敗しました。\n再度お試しいただくか、管理者にお問い合わせください。',
          { variant: 'error' }
        );
      } else {
        // Fallback: show generic error at top
        enqueueSnackbar('ログインに失敗しました。再度お試しください。', { variant: 'error' });
      }
      console.error(error.message);
      if (isMountedRef.current) {
        const errorMsg = getAuthErrorMessage(error);
        setError('afterSubmit', { ...error, message: errorMsg });
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}
        <RHFTextField
          name="email"
          label="メールアドレスを入力"
          type="email"
          autoComplete="username"
        />
        <RHFTextField
          name="password"
          label="パスワードを入力"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <Box sx={{ my: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <NextLink href={PATH_AUTH.resetPassword} passHref legacyBehavior>
          <Link variant="subtitle2">パスワードを忘れた方はこちら</Link>
        </NextLink>
      </Box>
      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        ログイン
      </LoadingButton>
    </FormProvider>
  );
}
